import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { getWhatsAppTemplates } from '../../api';
import { CustomSelect } from '@/components';
import { TemplatePhonePreview } from '../../components/TemplatePhonePreview';
import { EmailTemplatePreview } from '../../components/EmailTemplatePreview';
import { formatReminderOffset } from '../utils';
import { generateTemplateSchema } from '../../utils/templateUtils';
import gmailIcon from '../../assets/gmail_icon.png';
import whatsappIcon from '../../assets/WhatsApp_icon.png';
import { useMessagingChannels } from '../../hooks/useMessagingChannels';
import { ChannelAvailabilityState } from '../../components/ChannelAvailabilityState';
import { usePermissions } from '@/common/hooks/usePermissions';
import { PERMISSIONS } from '@/common/config/permissions';

// ---------------------------------------------------------------------------
// Constants & helpers
// ---------------------------------------------------------------------------

const PLACEHOLDERS = [
  '{{name}}',
  '{{time}}',
  '{{date}}',
  '{{datetime}}',
  '{{employee}}',
  '{{company}}',
  '{{meet_link}}',
];

const DEFAULT_BODY_VARIABLES = {
  1: '{{name}}',
  2: '{{time}}',
  3: '{{employee}}',
  4: '{{company}}',
};

function isTextOnlyTemplate(template) {
  const schema = generateTemplateSchema(template);
  if (schema.media_field || schema.carousel_media?.length) return false;
  const components = Array.isArray(template?.components)
    ? template.components
    : [];
  if (components.some((component) => component.type === 'CAROUSEL')) {
    return false;
  }
  if (Array.isArray(template?.cards) && template.cards.length > 0) {
    return false;
  }
  return true;
}

function bodySlotNumbers(template) {
  return generateTemplateSchema(template)
    .form_fields.filter((field) => field.id.startsWith('body_var_'))
    .map((field) => field.id.replace('body_var_', ''));
}

function variablesForSlots(slots, previous = {}) {
  const next = {};
  slots.forEach((slot) => {
    next[slot] = previous[slot] || DEFAULT_BODY_VARIABLES[slot] || '';
  });
  return next;
}

const PLACEHOLDER_SAMPLES = {
  '{{name}}': 'John Doe',
  '{{time}}': '10:00 AM',
  '{{date}}': '25 Sep 2026',
  '{{datetime}}': '25 Sep 2026 • 10:00 AM',
  '{{employee}}': 'Dr. Smith',
  '{{company}}': 'Aexon',
  '{{meet_link}}': 'https://meet.google.com/abc-defg-hij',
};

function previewValuesFromMapping(variables = {}) {
  const values = {};
  Object.entries(variables).forEach(([slot, token]) => {
    const sample = PLACEHOLDER_SAMPLES[token];
    if (sample) values[`body_var_${slot}`] = sample;
  });
  return values;
}

const formatSampleMessage = (raw) => {
  if (!raw) return '';
  return raw
    .replace(/\{\{datetime\}\}/gi, '25 Sep 2026 • 10:00 AM')
    .replace(
      /\{\{(name|customer_name|customer|client|patient)\}\}/gi,
      'John Doe',
    )
    .replace(/\{\{(date|appointment_date)\}\}/gi, '25 Sep 2026')
    .replace(/\{\{(time|appointment_time)\}\}/gi, '10:00 AM')
    .replace(/\{\{(agent_name|agent|employee|staff|doctor)\}\}/gi, 'Dr. Smith')
    .replace(/\{\{(company|business|clinic)\}\}/gi, 'Aexon')
    .replace(/\{\{meet_link\}\}/gi, 'https://meet.google.com/abc-defg-hij')
    .replace(/\{\{(service|treatment)\}\}/gi, 'General Consultation')
    .replace(/\{\{([^}]+)\}\}/g, '$1');
};

const DEFAULT_REMINDER = {
  id: 'rem_1',
  offset_minutes: 60,
  is_enabled: true,
  channels: ['whatsapp'],
  template_name: '',
  template_language: 'en_US',
  message_template:
    'Hi {{name}}, this is a friendly reminder for your appointment on {{date}} at {{time}} with {{employee}}.',
  email_subject: 'Reminder: Your appointment with {{company}}',
  email_body_template:
    'Hi {{name}},\n\nThis is a reminder that your appointment is scheduled for {{datetime}} with {{employee}}.\nJoin here: {{meet_link}}',
};

const TIMING_OPTIONS = [
  { label: '5 Min', sublabel: 'Before', value: 5 },
  { label: '15 Min', sublabel: 'Before', value: 15 },
  { label: '30 Min', sublabel: 'Before', value: 30 },
  { label: '1 Hour', sublabel: 'Before', value: 60 },
  { label: '2 Hours', sublabel: 'Before', value: 120 },
  { label: '1 Day', sublabel: 'Before', value: 1440 },
  { label: '2 Days', sublabel: 'Before', value: 2880 },
  { label: 'Custom', sublabel: 'Set your own', value: 'custom' },
];

function SectionCard({ children, className = '' }) {
  return (
    <div
      className={`bg-white rounded-2xl border border-border-default/70 shadow-[0_1px_4px_0_rgba(0,0,0,0.06)] overflow-visible flex-none h-auto ${className}`}
    >
      {children}
    </div>
  );
}

function SectionHeader({ icon, title, description }) {
  return (
    <div className="px-5 py-4 border-b border-border-default/50">
      <div className="flex items-center gap-2.5">
        {icon && <span className="text-lg leading-none">{icon}</span>}
        <div>
          <h3 className="text-sm font-semibold text-text-primary leading-tight">
            {title}
          </h3>
          {description && (
            <p className="text-xs text-text-secondary mt-0.5 leading-relaxed">
              {description}
            </p>
          )}
        </div>
      </div>
    </div>
  );
}

function Toggle({ checked, onChange, id, ariaLabel }) {
  return (
    <label
      htmlFor={id}
      className="relative inline-flex items-center cursor-pointer flex-shrink-0"
      aria-label={ariaLabel}
    >
      <input
        id={id}
        type="checkbox"
        checked={checked}
        onChange={onChange}
        className="sr-only peer"
      />
      <div
        className={`w-11 h-6 rounded-full transition-colors duration-200 relative ${
          checked ? 'bg-primary' : 'bg-border-default'
        }`}
      >
        <span
          className={`absolute top-1 left-1 w-4 h-4 bg-white rounded-full shadow-sm transition-transform duration-200 ${
            checked ? 'translate-x-5' : 'translate-x-0'
          }`}
        />
      </div>
    </label>
  );
}

function VariableMapper({ title, slots, variables, onChange }) {
  return (
    <div>
      <label className="block text-xs font-semibold text-text-secondary mb-2">
        {title}
      </label>
      <div className="flex flex-col gap-2">
        {slots.map((slot) => (
          <div
            key={slot}
            className="grid grid-cols-[80px_1fr] gap-2 items-center"
          >
            <span className="text-xs font-mono font-medium text-text-primary bg-surface-mist px-2 py-1 rounded-md text-center">
              {`{{${slot}}}`}
            </span>
            <CustomSelect
              value={variables[slot] || ''}
              onChange={(val) => onChange({ ...variables, [slot]: val })}
              options={PLACEHOLDERS.map((token) => ({
                value: token,
                label: token,
              }))}
              placeholder="Map variable..."
              className="w-full"
              buttonClassName="w-full h-9 px-3 flex items-center justify-between rounded-lg bg-white border border-border-default text-xs text-text-primary"
            />
          </div>
        ))}
      </div>
      <p className="text-[11px] text-text-secondary mt-1.5">
        Map {'{{meet_link}}'} only when this template has a slot for it.
      </p>
    </div>
  );
}

function EmailPreview({ subject, body }) {
  const formattedSubject =
    formatSampleMessage(subject) || 'Reminder: Your appointment with Aexon';
  const formattedBody =
    formatSampleMessage(body) ||
    'Hi John Doe,\n\nThis is a reminder for your upcoming appointment on 25 Sep 2026 • 10:00 AM with Dr. Smith.\nJoin here: https://meet.google.com/abc-defg-hij';

  return (
    <EmailTemplatePreview
      subject={formattedSubject}
      body={formattedBody}
      headerTitle="Appointment Email Preview"
      recipientEmail="john.doe@email.com"
      className="w-full max-w-[340px]"
    />
  );
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

export function AppointmentReminderSettings({
  reminders = [],
  onChange,
  companyId,
}) {
  const integration = useMessagingChannels(companyId);
  const { can } = usePermissions();
  const [customMode, setCustomMode] = useState(false);
  const [advancedOpen, setAdvancedOpen] = useState(false);

  const { data: templatesResponse } = useQuery({
    queryKey: ['whatsapp-templates', companyId],
    queryFn: () => getWhatsAppTemplates(companyId),
    enabled: !!companyId,
  });

  const availableTemplates = (
    templatesResponse?.results ||
    (Array.isArray(templatesResponse) ? templatesResponse : [])
  ).filter(isTextOnlyTemplate);

  const reminder =
    Array.isArray(reminders) && reminders.length > 0
      ? reminders[0]
      : DEFAULT_REMINDER;

  const currentChannels = Array.isArray(reminder.channels)
    ? reminder.channels
    : [reminder.channel || 'whatsapp'];

  const [previewTab, setPreviewTab] = useState(
    currentChannels.includes('whatsapp') ? 'template' : 'email',
  );

  const selectedTemplate = availableTemplates.find(
    (t) => t.name === reminder?.template_name,
  );
  const reminderBodySlots = bodySlotNumbers(selectedTemplate);

  const isEnabled = reminder.is_enabled !== false;

  const offsetNum = Number(reminder.offset_minutes);
  const isPreset = TIMING_OPTIONS.some(
    (o) => o.value !== 'custom' && Number(o.value) === offsetNum,
  );
  const isCustom = customMode || !isPreset;

  const handleUpdateReminder = (fields) => {
    onChange([{ ...reminder, ...fields }]);
  };

  useEffect(() => {
    if (!integration.isReady) return;
    const next = currentChannels.filter(
      (ch) =>
        (ch === 'email' && integration.email) ||
        (ch === 'whatsapp' && integration.whatsapp),
    );
    if (next.length === currentChannels.length) return;
    handleUpdateReminder({ channels: next, channel: next[0] || '' });
    // currentChannels identity changes with parent draft; compare by length above
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [integration.isReady, integration.email, integration.whatsapp]);

  const toggleChannel = (ch) => {
    if (
      !integration.isReady ||
      (ch === 'email' && !integration.email) ||
      (ch === 'whatsapp' && !integration.whatsapp)
    ) {
      return;
    }
    let next = [...currentChannels];
    if (next.includes(ch)) {
      if (next.length > 1) {
        next = next.filter((c) => c !== ch);
      }
    } else {
      next.push(ch);
    }
    handleUpdateReminder({ channels: next, channel: next[0] });
    if (!next.includes('whatsapp') && previewTab !== 'email') {
      setPreviewTab('email');
    } else if (!next.includes('email') && previewTab === 'email') {
      setPreviewTab('template');
    }
  };

  const handleTimingSelect = (option) => {
    if (option.value === 'custom') {
      setCustomMode(true);
      return;
    }
    setCustomMode(false);
    handleUpdateReminder({ offset_minutes: option.value });
  };

  const isTimingSelected = (option) => {
    if (option.value === 'custom') return isCustom;
    return !isCustom && Number(option.value) === offsetNum;
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-[1fr_360px] xl:grid-cols-[1fr_380px] gap-6 h-auto items-start">
      {/* LEFT: Configuration */}
      <div className="flex flex-col gap-4 w-full h-auto self-start pb-2">
        {/* Header Card */}
        <SectionCard>
          <div className="px-5 py-4 flex items-center justify-between gap-4">
            <div className="flex items-center gap-3 min-w-0">
              <div className="w-10 h-10 rounded-full bg-[#E7F8EE] flex items-center justify-center shrink-0 shadow-2xs">
                <img
                  src={whatsappIcon}
                  alt="WhatsApp"
                  className="w-6 h-6 object-contain"
                />
              </div>
              <div>
                <h2 className="text-base font-bold text-[#1F2241] leading-tight">
                  Automated Appointment Reminders
                </h2>
                <p className="text-xs text-[#868686] mt-0.5 leading-relaxed">
                  Automatically remind customers about upcoming appointments via
                  WhatsApp, Email, or Both.
                </p>
              </div>
            </div>

            <div className="flex flex-col items-center gap-1 flex-shrink-0">
              <Toggle
                id="reminder-master-toggle"
                checked={isEnabled}
                onChange={(e) =>
                  handleUpdateReminder({ is_enabled: e.target.checked })
                }
                ariaLabel={isEnabled ? 'Disable reminders' : 'Enable reminders'}
              />
              <span
                className={`text-[11px] font-semibold ${
                  isEnabled ? 'text-[#22C55E]' : 'text-[#868686]'
                }`}
              >
                {isEnabled ? 'Enabled' : 'Disabled'}
              </span>
            </div>
          </div>
        </SectionCard>

        {isEnabled && (
          <>
            {/* Section 1: Reminder Timing */}
            <SectionCard>
              <div className="px-5 py-4 border-b border-[#EBF4EA]">
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 rounded-full bg-[#FEF2F2] flex items-center justify-center shrink-0">
                    <span className="text-base">⏰</span>
                  </div>
                  <div>
                    <h3 className="text-sm font-bold text-[#1F2241] leading-tight">
                      When should we send the reminder?
                    </h3>
                    <p className="text-xs text-[#868686] mt-0.5 leading-relaxed">
                      Choose how far in advance customers are notified.
                    </p>
                  </div>
                </div>
              </div>
              <div className="px-5 py-4">
                <div className="grid grid-cols-2 sm:grid-cols-4 xl:grid-cols-8 gap-2.5 items-stretch">
                  {TIMING_OPTIONS.map((option) => {
                    const selected = isTimingSelected(option);
                    return (
                      <button
                        key={option.value}
                        type="button"
                        id={`timing-option-${option.value}`}
                        onClick={() => handleTimingSelect(option)}
                        className={`
                          flex flex-col items-center justify-center gap-0.5
                          rounded-2xl border-2 py-3 px-2 text-center
                          transition-all duration-150 cursor-pointer min-h-[76px] relative
                          ${
                            selected
                              ? 'border-[#22C55E] bg-white shadow-xs'
                              : 'border-[#EBF4EA] bg-white hover:border-[#22C55E]/40 hover:bg-[#F5F9F6]'
                          }
                        `}
                      >
                        <span
                          className={`text-sm font-bold leading-tight ${
                            selected ? 'text-[#22C55E]' : 'text-[#1F2241]'
                          }`}
                        >
                          {option.label}
                        </span>
                        <span
                          className={`text-[10px] font-medium leading-tight ${
                            selected ? 'text-[#22C55E]/80' : 'text-[#868686]'
                          }`}
                        >
                          {option.sublabel}
                        </span>
                        {selected && (
                          <span className="w-4 h-4 rounded-full bg-[#22C55E] flex items-center justify-center mt-1 text-white shadow-2xs">
                            <svg
                              viewBox="0 0 12 12"
                              className="w-2.5 h-2.5 stroke-white fill-none stroke-[2]"
                            >
                              <path
                                d="M2.5 6L5 8.5L9.5 3.5"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              />
                            </svg>
                          </span>
                        )}
                      </button>
                    );
                  })}
                </div>

                {isCustom && (
                  <div className="mt-4 flex items-center gap-3 p-3.5 bg-surface-mist rounded-xl border border-border-default/60">
                    <span className="text-xs text-text-secondary whitespace-nowrap">
                      Send reminder
                    </span>
                    <input
                      type="number"
                      min="1"
                      max="10080"
                      value={reminder.offset_minutes || ''}
                      onChange={(e) =>
                        handleUpdateReminder({
                          offset_minutes: Math.max(
                            1,
                            parseInt(e.target.value, 10) || 1,
                          ),
                        })
                      }
                      className="w-20 h-9 px-3 rounded-lg border border-border-default bg-white text-sm font-semibold text-text-primary focus:outline-none focus:border-primary text-center"
                    />
                    <span className="text-xs text-text-secondary whitespace-nowrap">
                      minutes before the appointment
                    </span>
                  </div>
                )}
              </div>
            </SectionCard>

            {/* Section 2: Channel Multi-Selection */}
            <SectionCard>
              <SectionHeader
                icon="📡"
                title="Select Notification Channels"
                description="Choose one or both channels to deliver this appointment reminder."
              />
              <div className="px-5 py-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {/* WhatsApp Checkbox Option */}
                  <ChannelAvailabilityState
                    status={integration}
                    canOpenSettings={can(PERMISSIONS.SETTINGS.VIEW)}
                  >
                    {integration.whatsapp ? (
                      <button
                        type="button"
                        onClick={() => toggleChannel('whatsapp')}
                        className={`flex items-center justify-between p-3.5 rounded-xl border-2 transition-all text-left cursor-pointer ${
                          currentChannels.includes('whatsapp')
                            ? 'border-[#25D366] bg-[#25D366]/5 shadow-xs'
                            : 'border-border-default hover:border-border-default/80 bg-white'
                        }`}
                      >
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-xl bg-[#25D366]/10 flex items-center justify-center flex-shrink-0">
                            <svg
                              viewBox="0 0 24 24"
                              className="w-5 h-5 fill-[#25D366]"
                            >
                              <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
                            </svg>
                          </div>
                          <div>
                            <p className="text-sm font-bold text-text-primary">
                              WhatsApp
                            </p>
                            <p className="text-[11px] text-text-secondary">
                              Meta approved templates & session text
                            </p>
                          </div>
                        </div>
                        <div
                          className={`w-5 h-5 rounded-md border flex items-center justify-center transition-colors ${
                            currentChannels.includes('whatsapp')
                              ? 'bg-[#25D366] border-[#25D366]'
                              : 'border-border-default bg-white'
                          }`}
                        >
                          {currentChannels.includes('whatsapp') && (
                            <svg
                              viewBox="0 0 12 12"
                              className="w-3 h-3 fill-white"
                            >
                              <path
                                d="M10 3L4.5 8.5 2 6"
                                stroke="white"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                fill="none"
                              />
                            </svg>
                          )}
                        </div>
                      </button>
                    ) : null}

                    {integration.email ? (
                      <button
                        type="button"
                        onClick={() => toggleChannel('email')}
                        className={`flex items-center justify-between p-3.5 rounded-xl border-2 transition-all text-left cursor-pointer ${
                          currentChannels.includes('email')
                            ? 'border-primary bg-primary/5 shadow-xs'
                            : 'border-border-default hover:border-border-default/80 bg-white'
                        }`}
                      >
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-xl bg-[#EA4335]/10 flex items-center justify-center shrink-0">
                            <img
                              src={gmailIcon}
                              alt="Gmail"
                              className="w-5 h-5 object-contain"
                            />
                          </div>
                          <div>
                            <p className="text-sm font-bold text-text-primary">
                              Email
                            </p>
                            <p className="text-[11px] text-text-secondary">
                              Gmail & Google Workspace API
                            </p>
                          </div>
                        </div>
                        <div
                          className={`w-5 h-5 rounded-md border flex items-center justify-center transition-colors ${
                            currentChannels.includes('email')
                              ? 'bg-primary border-primary'
                              : 'border-border-default bg-white'
                          }`}
                        >
                          {currentChannels.includes('email') && (
                            <svg
                              viewBox="0 0 12 12"
                              className="w-3 h-3 fill-white"
                            >
                              <path
                                d="M10 3L4.5 8.5 2 6"
                                stroke="white"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                fill="none"
                              />
                            </svg>
                          )}
                        </div>
                      </button>
                    ) : null}
                  </ChannelAvailabilityState>
                </div>
              </div>
            </SectionCard>

            {/* WhatsApp Config (when WhatsApp checked) */}
            {currentChannels.includes('whatsapp') && (
              <>
                <SectionCard>
                  <SectionHeader
                    icon="💬"
                    title="WhatsApp Reminder Configuration"
                    description="Select a pre-approved WhatsApp template for the reminder."
                  />
                  <div className="px-5 py-4 flex flex-col gap-3">
                    <div>
                      <label
                        htmlFor="template-select"
                        className="block text-xs font-semibold text-text-secondary mb-1.5"
                      >
                        Select Template{' '}
                        <span className="text-status-error">*</span>
                      </label>
                      <div
                        id="template-select"
                        onClick={() => setPreviewTab('template')}
                      >
                        {availableTemplates.length > 0 ? (
                          <CustomSelect
                            value={reminder.template_name || ''}
                            onChange={(val) => {
                              setPreviewTab('template');
                              const matched = availableTemplates.find(
                                (t) => t.name === val,
                              );
                              const slots = bodySlotNumbers(matched);
                              handleUpdateReminder({
                                template_name: val,
                                ...(matched?.language
                                  ? { template_language: matched.language }
                                  : {}),
                                template_variables: variablesForSlots(slots),
                              });
                            }}
                            options={availableTemplates.map((t) => ({
                              value: t.name,
                              label: `${t.name}${t.language ? ` (${t.language})` : ''}`,
                            }))}
                            placeholder="Choose a WhatsApp template..."
                            searchable
                            searchPlaceholder="Search templates..."
                            className="w-full"
                            buttonClassName="w-full h-11 px-3.5 flex items-center justify-between rounded-xl bg-white border border-border-default focus:border-primary text-sm text-text-primary focus:outline-none transition-all"
                          />
                        ) : (
                          <input
                            type="text"
                            value={reminder.template_name || ''}
                            onFocus={() => setPreviewTab('template')}
                            onChange={(e) => {
                              setPreviewTab('template');
                              handleUpdateReminder({
                                template_name: e.target.value,
                              });
                            }}
                            placeholder="e.g. appointment_reminder_1h"
                            className="w-full h-11 px-3.5 rounded-xl border border-border-default bg-white text-sm text-text-primary focus:outline-none focus:border-primary"
                          />
                        )}
                      </div>

                      {isEnabled && !reminder.template_name?.trim() ? (
                        <p className="text-[11px] text-status-error mt-1.5 font-medium">
                          A WhatsApp template is required for sending WhatsApp
                          reminders.
                        </p>
                      ) : selectedTemplate?.language ? (
                        <p className="text-[11px] text-text-secondary mt-1.5 flex items-center gap-1">
                          <span className="inline-block w-1.5 h-1.5 rounded-full bg-[#25D366]" />
                          Language: {selectedTemplate.language}
                        </p>
                      ) : null}
                    </div>

                    {reminderBodySlots.length > 0 && (
                      <div className="pt-3 border-t border-border-default/50">
                        <VariableMapper
                          title="Map template variables"
                          slots={reminderBodySlots}
                          variables={reminder.template_variables || {}}
                          onChange={(template_variables) =>
                            handleUpdateReminder({ template_variables })
                          }
                        />
                      </div>
                    )}
                  </div>
                </SectionCard>

                {/* Advanced WhatsApp Settings */}
                <SectionCard>
                  <button
                    type="button"
                    id="advanced-settings-toggle"
                    onClick={() => setAdvancedOpen((v) => !v)}
                    className="w-full px-5 py-4 flex items-center justify-between text-left transition-colors hover:bg-surface-mist/50"
                    aria-expanded={advancedOpen}
                  >
                    <div className="flex items-center gap-2.5">
                      <span className="text-base leading-none">⚙️</span>
                      <div>
                        <p className="text-sm font-semibold text-text-primary">
                          WhatsApp Advanced Settings
                        </p>
                        <p className="text-xs text-text-secondary mt-0.5">
                          Fallback text for open 24h WhatsApp conversation
                          windows.
                        </p>
                      </div>
                    </div>
                    <svg
                      viewBox="0 0 20 20"
                      className={`w-4 h-4 text-text-secondary transition-transform duration-200 flex-shrink-0 ${
                        advancedOpen ? 'rotate-180' : ''
                      }`}
                      fill="currentColor"
                    >
                      <path
                        fillRule="evenodd"
                        d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                        clipRule="evenodd"
                      />
                    </svg>
                  </button>

                  {advancedOpen && (
                    <div className="px-5 pb-5 flex flex-col gap-4 border-t border-border-default/50">
                      <div className="pt-4">
                        <label className="block text-xs font-semibold text-text-secondary mb-1">
                          Fallback Message
                        </label>
                        <p className="text-[11px] text-text-secondary mb-2 leading-relaxed">
                          Sent as a free text message when customer messaged in
                          last 24 hours.
                        </p>
                        <textarea
                          rows={4}
                          value={reminder.message_template || ''}
                          onFocus={() => setPreviewTab('message')}
                          onChange={(e) => {
                            setPreviewTab('message');
                            handleUpdateReminder({
                              message_template: e.target.value,
                            });
                          }}
                          placeholder="Hi {{name}}, your appointment starts at {{time}}!"
                          className="w-full p-3.5 rounded-xl border border-border-default bg-white text-sm text-text-primary placeholder:text-text-secondary/50 focus:outline-none focus:border-primary leading-relaxed resize-none"
                        />

                        <p className="text-[11px] font-semibold text-text-secondary mt-2 mb-1.5">
                          Insert variable:
                        </p>
                        <div className="flex flex-wrap gap-1.5">
                          {PLACEHOLDERS.map((token) => (
                            <button
                              key={token}
                              type="button"
                              onClick={() => {
                                setPreviewTab('message');
                                const current = reminder.message_template || '';
                                const spacer =
                                  current && !/\s$/.test(current) ? ' ' : '';
                                handleUpdateReminder({
                                  message_template: `${current}${spacer}${token}`,
                                });
                              }}
                              className="h-6 px-2.5 rounded-full border border-border-default bg-white text-[11px] font-medium text-text-primary hover:border-primary/60 hover:bg-primary/5 transition-colors"
                            >
                              {token}
                            </button>
                          ))}
                        </div>
                      </div>
                    </div>
                  )}
                </SectionCard>
              </>
            )}

            {/* Email Config (when Email checked) */}
            {currentChannels.includes('email') && (
              <SectionCard>
                <SectionHeader
                  icon="✉️"
                  title="Email Reminder Configuration"
                  description="Customize the email subject line and body template for customer reminders."
                />
                <div className="px-5 py-4 flex flex-col gap-4">
                  <div>
                    <label className="block text-xs font-semibold text-text-secondary mb-1.5">
                      Email Subject <span className="text-status-error">*</span>
                    </label>
                    <input
                      type="text"
                      value={reminder.email_subject || ''}
                      onFocus={() => setPreviewTab('email')}
                      onChange={(e) => {
                        setPreviewTab('email');
                        handleUpdateReminder({ email_subject: e.target.value });
                      }}
                      placeholder="e.g. Reminder: Your appointment with {{company}}"
                      className="w-full h-11 px-3.5 rounded-xl border border-border-default bg-white text-sm text-text-primary focus:outline-none focus:border-primary"
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-semibold text-text-secondary mb-1.5">
                      Email Body Template{' '}
                      <span className="text-status-error">*</span>
                    </label>
                    <textarea
                      rows={6}
                      value={reminder.email_body_template || ''}
                      onFocus={() => setPreviewTab('email')}
                      onChange={(e) => {
                        setPreviewTab('email');
                        handleUpdateReminder({
                          email_body_template: e.target.value,
                        });
                      }}
                      placeholder="Hi {{name}},\n\nThis is a reminder for your upcoming appointment on {{datetime}} with {{employee}}.\nJoin here: {{meet_link}}"
                      className="w-full p-3.5 rounded-xl border border-border-default bg-white text-sm text-text-primary placeholder:text-text-secondary/50 focus:outline-none focus:border-primary leading-relaxed resize-none"
                    />

                    <p className="text-[11px] font-semibold text-text-secondary mt-2 mb-1.5">
                      Insert variable placeholder:
                    </p>
                    <div className="flex flex-wrap gap-1.5">
                      {PLACEHOLDERS.map((token) => (
                        <button
                          key={token}
                          type="button"
                          onClick={() => {
                            setPreviewTab('email');
                            const current = reminder.email_body_template || '';
                            const spacer =
                              current && !/\s$/.test(current) ? ' ' : '';
                            handleUpdateReminder({
                              email_body_template: `${current}${spacer}${token}`,
                            });
                          }}
                          className="h-6 px-2.5 rounded-full border border-border-default bg-white text-[11px] font-medium text-text-primary hover:border-primary/60 hover:bg-primary/5 transition-colors"
                        >
                          {token}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              </SectionCard>
            )}
          </>
        )}

        {!isEnabled && (
          <div className="flex flex-col items-center justify-center py-12 text-center bg-white rounded-2xl border border-border-default/70 border-dashed">
            <span className="text-4xl mb-3">💤</span>
            <p className="text-sm font-semibold text-text-primary">
              Reminders are disabled
            </p>
            <p className="text-xs text-text-secondary mt-1 max-w-xs">
              Enable the toggle above to configure WhatsApp or Email appointment
              reminders for your customers.
            </p>
          </div>
        )}
      </div>

      {/* RIGHT COLUMN: Live Channel Preview */}
      <div className="bg-white rounded-2xl border border-border-default/70 p-4 sm:p-5 flex flex-col items-center gap-4 w-full h-auto self-start lg:sticky lg:top-0 shadow-sm">
        <div className="w-full flex flex-col gap-2.5 border-b border-border-default/60 pb-3.5">
          <div className="flex items-center justify-between gap-2">
            <div>
              <h3 className="text-sm font-bold text-[#1F2241]">Live Preview</h3>
              <p className="text-[11px] font-medium text-[#868686] mt-0.5">
                {formatReminderOffset(reminder.offset_minutes)}
              </p>
            </div>
            <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200/60 uppercase tracking-wider">
              {previewTab === 'email' ? 'Email' : 'WhatsApp'}
            </span>
          </div>

          {/* Full-width segmented channel tabs */}
          <div className="w-full grid grid-flow-col auto-cols-fr bg-[#F5F5F5] p-1 rounded-xl text-xs gap-1">
            {currentChannels.includes('whatsapp') && (
              <>
                <button
                  type="button"
                  onClick={() => setPreviewTab('template')}
                  className={`py-1.5 px-2 rounded-lg text-[11px] font-semibold transition-all text-center truncate ${
                    previewTab === 'template'
                      ? 'bg-white text-[#1F2241] shadow-xs'
                      : 'text-[#868686] hover:text-[#1F2241]'
                  }`}
                >
                  Meta Template
                </button>
                <button
                  type="button"
                  onClick={() => setPreviewTab('message')}
                  className={`py-1.5 px-2 rounded-lg text-[11px] font-semibold transition-all text-center truncate ${
                    previewTab === 'message'
                      ? 'bg-white text-[#1F2241] shadow-xs'
                      : 'text-[#868686] hover:text-[#1F2241]'
                  }`}
                >
                  Free Message
                </button>
              </>
            )}
            {currentChannels.includes('email') && (
              <button
                type="button"
                onClick={() => setPreviewTab('email')}
                className={`py-1.5 px-2 rounded-lg text-[11px] font-semibold transition-all text-center truncate ${
                  previewTab === 'email'
                    ? 'bg-white text-[#1F2241] shadow-xs'
                    : 'text-[#868686] hover:text-[#1F2241]'
                }`}
              >
                Email
              </button>
            )}
          </div>
        </div>

        {/* Live Preview Display */}
        <div className="w-full flex justify-center py-2">
          {previewTab === 'email' ? (
            <EmailPreview
              subject={reminder.email_subject}
              body={reminder.email_body_template}
            />
          ) : (
            <div className="reminder-phone-slot">
              {previewTab === 'template' ? (
                selectedTemplate ? (
                  <TemplatePhonePreview
                    className="reminder-phone"
                    template={selectedTemplate}
                    templateValues={previewValuesFromMapping(
                      reminder.template_variables,
                    )}
                  />
                ) : (
                  <TemplatePhonePreview
                    className="reminder-phone"
                    message={
                      reminder?.template_name
                        ? `Template: ${reminder.template_name}\n\nHi John Doe, reminder for your appointment on 25 Sep 2026 at 10:00 AM with Dr. Smith.`
                        : 'Select a WhatsApp Meta Template on the left to see the live template preview.'
                    }
                  />
                )
              ) : (
                <TemplatePhonePreview
                  className="reminder-phone"
                  message={
                    reminder?.message_template !== undefined &&
                    reminder?.message_template !== null
                      ? formatSampleMessage(reminder.message_template)
                      : 'Hi John Doe, reminder for your appointment on 25 Sep 2026 at 10:00 AM with Dr. Smith.'
                  }
                />
              )}
            </div>
          )}
        </div>

        <div className="w-full text-center text-[11px] text-text-secondary bg-[#F8F9FA] rounded-xl p-2.5 border border-border-default/40">
          {previewTab === 'email' ? (
            <span>
              ✉️ <strong>Email Channel:</strong> Dispatched via connected Google
              Workspace / Gmail account.
            </span>
          ) : previewTab === 'template' ? (
            <span>
              🛡️ <strong>WhatsApp Template:</strong> Sent automatically when
              contact is outside the 24h window.
            </span>
          ) : (
            <span>
              ⚡ <strong>WhatsApp Session:</strong> Free message sent when
              contact messaged in last 24h.
            </span>
          )}
        </div>
      </div>
    </div>
  );
}
